
class DiceAudioManager {
  private audioFiles: HTMLAudioElement[] = [];
  private soundCount = 6; // We have diceRoll1.mp3 to diceRoll6.mp3
  private isPreloaded = false;
  private fallbackAudio: HTMLAudioElement | null = null;
  private lastPlayedIndex: number = -1;

  constructor() {
    this.preloadSounds();
  }

  private preloadSounds() {
    console.log("DiceAudioManager: Preloading dice sounds...");
    
    // Create an array to track loading promises
    const loadPromises: Promise<Event>[] = [];
    
    // Preload the main dice sound files
    for (let i = 1; i <= this.soundCount; i++) {
      try {
        const audio = new Audio(`/diceRoll${i}.mp3`);
        audio.volume = 0.5;
        audio.preload = 'auto';
        
        // Create a promise for this audio file loading
        const loadPromise = new Promise<Event>((resolve, reject) => {
          audio.addEventListener('canplaythrough', (e) => {
            console.log(`DiceAudioManager: diceRoll${i}.mp3 loaded successfully`);
            resolve(e);
          }, { once: true });
          
          audio.addEventListener('error', (e) => {
            console.warn(`DiceAudioManager: Failed to load diceRoll${i}.mp3:`, e);
            reject(e);
          }, { once: true });
        });
        
        loadPromises.push(loadPromise);
        this.audioFiles.push(audio);
      } catch (error) {
        console.warn(`DiceAudioManager: Error creating audio for diceRoll${i}.mp3:`, error);
      }
    }
    
    // Set up fallback audio (the original dice-roll.mp3)
    try {
      this.fallbackAudio = new Audio("/dice-roll.mp3");
      this.fallbackAudio.volume = 0.5;
      this.fallbackAudio.preload = 'auto';
      
      // Add fallback to loading promises
      const fallbackPromise = new Promise<Event>((resolve, reject) => {
        if (this.fallbackAudio) {
          this.fallbackAudio.addEventListener('canplaythrough', (e) => {
            console.log("DiceAudioManager: Fallback audio loaded");
            resolve(e);
          }, { once: true });
          
          this.fallbackAudio.addEventListener('error', (e) => {
            console.warn("DiceAudioManager: Failed to load fallback audio:", e);
            reject(e);
          }, { once: true });
        } else {
          reject(new Error("No fallback audio"));
        }
      });
      
      loadPromises.push(fallbackPromise);
    } catch (error) {
      console.warn("DiceAudioManager: Failed to load fallback audio:", error);
    }
    
    // Mark as preloaded when all or at least some files are loaded
    Promise.allSettled(loadPromises).then((results) => {
      const loadedCount = results.filter(r => r.status === 'fulfilled').length;
      console.log(`DiceAudioManager: Preloaded ${loadedCount}/${loadPromises.length} audio files`);
      this.isPreloaded = loadedCount > 0;
    });
  }

  public async playRandomDiceSound(): Promise<void> {
    try {
      // Try to play a random dice sound from our collection, but not the same as the last one
      if (this.audioFiles.length > 0) {
        let randomIndex;
        
        // Make sure we don't play the same sound twice in a row
        do {
          randomIndex = Math.floor(Math.random() * this.audioFiles.length);
        } while (randomIndex === this.lastPlayedIndex && this.audioFiles.length > 1);
        
        const selectedAudio = this.audioFiles[randomIndex];
        this.lastPlayedIndex = randomIndex;
        
        console.log(`DiceAudioManager: Playing dice sound ${randomIndex + 1}`);
        
        // Reset audio to start and play
        selectedAudio.currentTime = 0;
        
        // Try to play the selected audio
        try {
          await selectedAudio.play();
          return;
        } catch (playError) {
          console.warn(`DiceAudioManager: Failed to play sound ${randomIndex + 1}, trying fallback:`, playError);
          throw playError; // Throw to trigger fallback
        }
      }
      
      // Fallback to original dice-roll.mp3
      if (this.fallbackAudio) {
        console.log("DiceAudioManager: Using fallback dice sound");
        this.fallbackAudio.currentTime = 0;
        await this.fallbackAudio.play();
        return;
      }
      
      console.warn("DiceAudioManager: No audio files available to play");
    } catch (error) {
      console.warn("DiceAudioManager: Audio playback failed:", error);
      // Silently fail - don't break the app if audio doesn't work
      
      // Try fallback as last resort
      if (this.fallbackAudio && error instanceof Error) {
        try {
          this.fallbackAudio.currentTime = 0;
          await this.fallbackAudio.play();
        } catch (fallbackError) {
          console.warn("DiceAudioManager: Fallback audio also failed:", fallbackError);
        }
      }
    }
  }

  public getLoadedSoundsCount(): number {
    return this.audioFiles.length;
  }

  public isReady(): boolean {
    return this.isPreloaded;
  }
}

// Create a singleton instance
const diceAudioManager = new DiceAudioManager();

// Export the function to play random dice sounds
export const playRandomDiceSound = () => diceAudioManager.playRandomDiceSound();

// Export utility functions for debugging
export const getDiceAudioInfo = () => ({
  loadedSounds: diceAudioManager.getLoadedSoundsCount(),
  isReady: diceAudioManager.isReady()
});
